/* Custom cut-corner floors. All stored and returned distances are metres.
   Pure geometry only: no editor, rendering, pricing, or unit-formatting state. */
(function (global) {
  'use strict';
  if (global.SwishCBCustomSurface) return;

  var CORNERS = ['topLeft', 'topRight', 'bottomRight', 'bottomLeft'];
  var MAX_DISTANCE_M = 1000000;
  var MAX_TILE_CELLS = 250000;

  function record(value) { return !!value && typeof value === 'object' && !Array.isArray(value); }
  function finite(value) {
    if (typeof value !== 'number' || !Number.isFinite(value)) return 0;
    return Math.min(MAX_DISTANCE_M, Math.max(0, value));
  }
  function dimensions(lengthM, widthM) {
    var length = finite(lengthM);
    var width = finite(widthM);
    return length > 0 && width > 0 ? { lengthM: length, widthM: width } : null;
  }
  function clamp(value, min, max) { return Math.min(Math.max(value, min), max); }

  function sanitize(value) {
    if (!record(value) || value.v !== 1) return null;
    var result = { v: 1, exactDimensions: value.exactDimensions !== false, corners: {}, hoopPlacement: {} };
    CORNERS.forEach(function (name) {
      if (!record(value.corners) || !Object.prototype.hasOwnProperty.call(value.corners, name)) return;
      var corner = record(value.corners[name]) ? value.corners[name] : {};
      var xM = finite(corner.xM);
      var yM = finite(corner.yM);
      // Keep each editable leg independently. A zero leg removes the triangle
      // from geometry while retaining the other value for incremental editing.
      result.corners[name] = { xM: xM, yM: yM };
    });
    ['left', 'right'].forEach(function (side) {
      var source = record(value.hoopPlacement) ? value.hoopPlacement[side] : null;
      if (!record(source)) return;
      result.hoopPlacement[side] = {
        centerFromTopM: finite(source.centerFromTopM),
        poleWidthM: finite(source.poleWidthM),
        poleDepthM: finite(source.poleDepthM),
        rearClearanceM: finite(source.rearClearanceM)
      };
    });
    return result;
  }

  function completeCorners(value) {
    var corners = {};
    CORNERS.forEach(function (name) {
      var source = value && value.corners ? value.corners[name] : null;
      corners[name] = source && source.xM > 0 && source.yM > 0
        ? { xM: source.xM, yM: source.yM } : { xM: 0, yM: 0 };
    });
    return corners;
  }

  function fitPair(first, second, key, limit) {
    var sum = first[key] + second[key];
    // Leave a positive straight edge even after a smaller overall size is entered.
    var available = limit * (1 - 1e-9);
    if (sum > available && sum > 0) {
      var scale = available / sum;
      first[key] *= scale;
      second[key] *= scale;
    }
  }

  function resolve(value, lengthM, widthM) {
    var result = sanitize(value);
    var size = dimensions(lengthM, widthM);
    if (!result || !size) return null;
    var cuts = completeCorners(result);
    CORNERS.forEach(function (name) {
      cuts[name].xM = Math.min(cuts[name].xM, size.lengthM);
      cuts[name].yM = Math.min(cuts[name].yM, size.widthM);
    });
    fitPair(cuts.topLeft, cuts.topRight, 'xM', size.lengthM);
    fitPair(cuts.bottomLeft, cuts.bottomRight, 'xM', size.lengthM);
    fitPair(cuts.topLeft, cuts.bottomLeft, 'yM', size.widthM);
    fitPair(cuts.topRight, cuts.bottomRight, 'yM', size.widthM);
    Object.keys(result.corners).forEach(function (name) {
      var original = result.corners[name];
      result.corners[name] = original.xM > 0 && original.yM > 0 ? cuts[name] : {
        xM: Math.min(original.xM, size.lengthM),
        yM: Math.min(original.yM, size.widthM)
      };
    });
    return result;
  }

  function polygon(value, lengthM, widthM) {
    var size = dimensions(lengthM, widthM);
    if (!size) return [];
    var custom = resolve(value, size.lengthM, size.widthM);
    if (!custom) return [
      { x: 0, y: 0 }, { x: size.lengthM, y: 0 },
      { x: size.lengthM, y: size.widthM }, { x: 0, y: size.widthM }
    ];
    var c = completeCorners(custom);
    var points = [
      { x: c.topLeft.xM, y: 0 },
      { x: size.lengthM - c.topRight.xM, y: 0 },
      { x: size.lengthM, y: c.topRight.yM },
      { x: size.lengthM, y: size.widthM - c.bottomRight.yM },
      { x: size.lengthM - c.bottomRight.xM, y: size.widthM },
      { x: c.bottomLeft.xM, y: size.widthM },
      { x: 0, y: size.widthM - c.bottomLeft.yM },
      { x: 0, y: c.topLeft.yM }
    ];
    return points.filter(function (point, index) {
      var previous = points[(index + points.length - 1) % points.length];
      return point.x !== previous.x || point.y !== previous.y;
    });
  }

  function polygonArea(points) {
    if (points.length < 3) return 0;
    // Translation avoids cancellation for narrow cells far from the origin.
    var origin = points[0];
    var sum = 0;
    for (var i = 1; i < points.length - 1; i += 1) {
      sum += (points[i].x - origin.x) * (points[i + 1].y - origin.y)
        - (points[i + 1].x - origin.x) * (points[i].y - origin.y);
    }
    return Math.abs(sum) / 2;
  }

  function area(value, lengthM, widthM) { return polygonArea(polygon(value, lengthM, widthM)); }

  function metrics(value, lengthM, widthM) {
    var size = dimensions(lengthM, widthM);
    if (!size) return null;
    var custom = resolve(value, size.lengthM, size.widthM);
    var corners = completeCorners(custom);
    var points = polygon(custom, size.lengthM, size.widthM);
    var perimeter = 0;
    for (var i = 0; i < points.length; i += 1) {
      var next = points[(i + 1) % points.length];
      perimeter += Math.hypot(next.x - points[i].x, next.y - points[i].y);
    }
    return {
      lengthM: size.lengthM, widthM: size.widthM,
      areaM2: polygonArea(points), perimeterM: perimeter,
      edges: {
        topM: size.lengthM - corners.topLeft.xM - corners.topRight.xM,
        rightM: size.widthM - corners.topRight.yM - corners.bottomRight.yM,
        bottomM: size.lengthM - corners.bottomLeft.xM - corners.bottomRight.xM,
        leftM: size.widthM - corners.topLeft.yM - corners.bottomLeft.yM
      },
      corners: corners, polygon: points
    };
  }

  function horizontalBounds(corners, y, lengthM, widthM) {
    var left = 0;
    var right = lengthM;
    if (corners.topLeft.yM > 0 && y < corners.topLeft.yM) {
      left = Math.max(left, corners.topLeft.xM * (1 - y / corners.topLeft.yM));
    }
    if (corners.bottomLeft.yM > 0 && y > widthM - corners.bottomLeft.yM) {
      left = Math.max(left, corners.bottomLeft.xM * (1 - (widthM - y) / corners.bottomLeft.yM));
    }
    if (corners.topRight.yM > 0 && y < corners.topRight.yM) {
      right = Math.min(right, lengthM - corners.topRight.xM * (1 - y / corners.topRight.yM));
    }
    if (corners.bottomRight.yM > 0 && y > widthM - corners.bottomRight.yM) {
      right = Math.min(right, lengthM - corners.bottomRight.xM * (1 - (widthM - y) / corners.bottomRight.yM));
    }
    return { left: left, right: right };
  }

  function resolveHoop(value, side, lengthM, widthM, overhangM) {
    var size = dimensions(lengthM, widthM);
    var custom = resolve(value, lengthM, widthM);
    if (!size || !custom || (side !== 'left' && side !== 'right')) return null;
    var source = custom.hoopPlacement[side];
    if (!source || source.poleWidthM <= 0 || source.poleDepthM <= 0) return null;
    var poleWidth = Math.min(source.poleWidthM, size.widthM);
    var centerY = clamp(source.centerFromTopM, poleWidth / 2, size.widthM - poleWidth / 2);
    var top = centerY - poleWidth / 2;
    var bottom = centerY + poleWidth / 2;
    var corners = completeCorners(custom);
    var topBounds = horizontalBounds(corners, top, size.lengthM, size.widthM);
    var bottomBounds = horizontalBounds(corners, bottom, size.lengthM, size.widthM);
    var minX = Math.max(topBounds.left, bottomBounds.left);
    var maxX = Math.min(topBounds.right, bottomBounds.right);
    var poleDepth = Math.min(source.poleDepthM, Math.max(0, maxX - minX));
    if (poleDepth <= 0) return null;
    var back = side === 'left'
      ? clamp(source.rearClearanceM, minX, maxX - poleDepth)
      : clamp(source.rearClearanceM, size.lengthM - maxX, size.lengthM - minX - poleDepth);
    var front = back + poleDepth;
    var overhang = finite(overhangM);
    var boardFront = front + overhang;
    var poleX = side === 'left' ? back : size.lengthM - front;
    return {
      side: side, centerFromTopM: centerY,
      poleWidthM: poleWidth, poleDepthM: poleDepth,
      rearClearanceM: back, overhangM: overhang,
      poleBackM: back, poleFrontM: front, boardFrontM: boardFront,
      poleRect: { x: poleX, y: top, width: poleDepth, height: poleWidth },
      center: { x: poleX + poleDepth / 2, y: centerY },
      board: { x: side === 'left' ? boardFront : size.lengthM - boardFront, y: centerY }
    };
  }

  function clipBoundary(points, inside, intersect) {
    var output = [];
    if (!points.length) return output;
    var previous = points[points.length - 1];
    var previousInside = inside(previous);
    points.forEach(function (current) {
      var currentInside = inside(current);
      if (currentInside !== previousInside) output.push(intersect(previous, current));
      if (currentInside) output.push(current);
      previous = current;
      previousInside = currentInside;
    });
    return output;
  }

  function intersectRectangle(points, x1, y1, x2, y2) {
    var result = points;
    function atX(a, b, x) { var t = (x - a.x) / (b.x - a.x); return { x: x, y: a.y + t * (b.y - a.y) }; }
    function atY(a, b, y) { var t = (y - a.y) / (b.y - a.y); return { x: a.x + t * (b.x - a.x), y: y }; }
    result = clipBoundary(result, function (p) { return p.x >= x1; }, function (a, b) { return atX(a, b, x1); });
    result = clipBoundary(result, function (p) { return p.x <= x2; }, function (a, b) { return atX(a, b, x2); });
    result = clipBoundary(result, function (p) { return p.y >= y1; }, function (a, b) { return atY(a, b, y1); });
    result = clipBoundary(result, function (p) { return p.y <= y2; }, function (a, b) { return atY(a, b, y2); });
    return result;
  }

  function tileCoverage(value, lengthM, widthM, tileM) {
    var size = dimensions(lengthM, widthM);
    var tile = finite(tileM);
    if (!size || tile <= 0) return null;
    // Tolerance prevents an extra row for exact unit conversions such as 30 ft.
    var columns = Math.ceil(size.lengthM / tile - 1e-10);
    var rows = Math.ceil(size.widthM / tile - 1e-10);
    if (columns < 1 || rows < 1 || columns * rows > MAX_TILE_CELLS) return null;
    var points = polygon(value, size.lengthM, size.widthM);
    var total = 0;
    var full = 0;
    var coveredArea = 0;
    var tileArea = tile * tile;
    var tolerance = tileArea * 1e-10;
    for (var row = 0; row < rows; row += 1) {
      for (var column = 0; column < columns; column += 1) {
        var pieceArea = polygonArea(intersectRectangle(points,
          column * tile, row * tile,
          Math.min(size.lengthM, (column + 1) * tile),
          Math.min(size.widthM, (row + 1) * tile)));
        if (pieceArea <= tileArea * 1e-12) continue;
        total += 1;
        coveredArea += pieceArea;
        if (Math.abs(pieceArea - tileArea) <= tolerance) full += 1;
      }
    }
    return {
      columns: columns, rows: rows, totalTiles: total, fullTiles: full,
      partialTiles: total - full, discardedTiles: columns * rows - total,
      areaM2: polygonArea(points), coveredAreaM2: coveredArea, tileAreaM2: tileArea
    };
  }

  global.SwishCBCustomSurface = {
    sanitize: sanitize, resolve: resolve, polygon: polygon, area: area,
    metrics: metrics, resolveHoop: resolveHoop, tileCoverage: tileCoverage
  };
})(typeof window !== 'undefined' ? window : globalThis);
